import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { useSubscription } from '../components/SubscriptionGuard';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { formatDuration } from '../lib/utils';
import { 
  Clock, 
  Play, 
  Square, 
  TrendingUp,
  TrendingDown,
  Calendar, 
  FolderKanban,
  ArrowRight,
  Timer,
  Activity,
  Zap,
  Target,
  Plus,
  BarChart3,
  Pencil,
  Trash2,
  AlertCircle,
  Trophy,
  CalendarDays,
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  Send,
  X,
  AlertTriangle,
  PartyPopper,
  Info,
  Flame,
  ArrowUpRight,
  Sparkles
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Dashboard = () => {
  const { api, user } = useAuth();
  const { isRunning, elapsedTime, formatTime, startTimer, stopTimer, continueEntry } = useTimer();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  const isStealthEmployee = user?.tracking_mode === 'stealth' && user?.role === 'employee';
  const [summary, setSummary] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [todaySummary, setTodaySummary] = useState(null);
  const [lastWeekSummary, setLastWeekSummary] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartFilter, setChartFilter] = useState('thisWeek');
  const [activityFilter, setActivityFilter] = useState('all');
  const [timesheetStatus, setTimesheetStatus] = useState(null);
  const [dailyHoursTarget, setDailyHoursTarget] = useState(8);
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    const today = toLocalDateStr(new Date());
    const stored = localStorage.getItem('dismissedAlerts');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.date !== today) return { date: today, ids: [] };
      return parsed;
    }
    return { date: today, ids: [] };
  });
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [approvedLeaveRequests, setApprovedLeaveRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const currentWeekStart = new Date(now);
        const dayOfWeek = currentWeekStart.getDay();
        currentWeekStart.setDate(currentWeekStart.getDate() - dayOfWeek);
        currentWeekStart.setHours(0, 0, 0, 0);
        
        const lastWeekStart = new Date(currentWeekStart);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(currentWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

        const [summaryRes, monthlyRes, todayRes, lastWeekRes, entriesRes, projectsRes] = await Promise.all([
          api.get('/reports/summary?period=week'),
          api.get('/reports/summary?period=month'),
          api.get('/reports/summary?period=day'),
          api.get(`/reports/summary?start_date=${lastWeekStart.toISOString()}&end_date=${lastWeekEnd.toISOString()}`),
          api.get('/time-entries'),
          api.get('/projects')
        ]);
        
        setSummary(summaryRes.data);
        setMonthlySummary(monthlyRes.data);
        setTodaySummary(todayRes.data);
        setLastWeekSummary(lastWeekRes.data);
        setRecentEntries(entriesRes.data);
        setProjects(projectsRes.data);
        
        try {
          const timesheetRes = await api.get('/approvals/current-week');
          setTimesheetStatus(timesheetRes.data);
        } catch (err) {
          console.error('Error fetching timesheet status:', err);
        }
        
        try {
          const profileRes = await api.get('/profile');
          if (profileRes.data?.preferences?.daily_hours_target) {
            setDailyHoursTarget(profileRes.data.preferences.daily_hours_target);
          }
        } catch (err) {
          console.error('Error fetching user preferences:', err);
        }
        
        try {
          const leaveRes = await api.get('/time-off/my-requests');
          const pending = leaveRes.data.filter(r => r.status === 'pending');
          const approved = leaveRes.data.filter(r => r.status === 'approved' && new Date(r.end_date) >= now);
          setPendingLeaveRequests(pending);
          setApprovedLeaveRequests(approved);
        } catch (err) {
          console.error('Error fetching leave requests:', err);
        }
        
        try {
          const holidayRes = await api.get('/holidays');
          setHolidays(holidayRes.data || []);
        } catch (err) {
          console.error('Error fetching holidays:', err);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTimeOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatFullDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
    });
  };

  const getProjectById = (id) => projects.find(p => p.id === id);

  const chartData = useMemo(() => {
    const now = new Date();
    const currentWeekStart = new Date(now);
    const dayOfWeek = currentWeekStart.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    currentWeekStart.setDate(currentWeekStart.getDate() - daysToMonday);
    currentWeekStart.setHours(0, 0, 0, 0);

    const dailyData = {};
    summary?.daily_breakdown?.forEach(d => {
      dailyData[d.date] = d.duration || 0;
    });

    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const todayIndex = now.getDay() === 0 ? 6 : now.getDay() - 1;

    return weekDays.map((day, index) => {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + index);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const duration = dailyData[dateStr] || 0;
      const hours = Math.round((duration / 3600) * 10) / 10;

      return { day, date: dateStr, hours, isToday: index === todayIndex, isFuture: index > todayIndex };
    });
  }, [summary]);

  const dailyAverage = useMemo(() => {
    const daysWithData = chartData.filter(d => d.hours > 0).length;
    if (daysWithData < 2) return null;
    const totalHours = chartData.reduce((acc, d) => acc + d.hours, 0);
    return Math.round((totalHours / 7) * 10) / 10;
  }, [chartData]);

  const validRecentEntries = useMemo(() => {
    const today = toLocalDateStr(new Date());
    let filtered = recentEntries.filter(entry => entry.duration && entry.duration >= 60);
    if (activityFilter === 'today') {
      filtered = filtered.filter(entry => entry.start_time?.slice(0, 10) === today);
    }
    return filtered.slice(0, 5);
  }, [recentEntries, activityFilter]);

  const weekTrend = useMemo(() => {
    const current = summary?.total_duration || 0;
    const previous = lastWeekSummary?.total_duration || 0;
    if (!previous) return null;
    const diff = current - previous;
    const pct = previous > 0 ? Math.round((diff / previous) * 100) : 0;
    return { value: Math.abs(diff), isUp: diff >= 0, pct: Math.abs(pct), formatted: formatDuration(Math.abs(diff)) };
  }, [summary, lastWeekSummary]);

  const monthHours = useMemo(() => monthlySummary?.total_duration || 0, [monthlySummary]);
  const todayHours = useMemo(() => todaySummary?.total_duration || 0, [todaySummary]);

  const uniqueProjectsThisWeek = useMemo(() => {
    const projectIds = new Set();
    summary?.project_breakdown?.forEach(p => {
      if (p.project_id) projectIds.add(p.project_id);
    });
    return projectIds.size || (summary?.project_breakdown?.length || 0);
  }, [summary]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
          <p className="font-medium opacity-70 mb-0.5">{label}</p>
          <p className="text-base font-bold timer-display">{payload[0].value}h</p>
        </div>
      );
    }
    return null;
  };

  const handleContinueEntry = (entry) => continueEntry(entry);

  const handleDeleteEntry = async (entryId) => {
    try {
      await api.delete(`/time-entries/${entryId}`);
      setRecentEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  const isWeekend = useMemo(() => {
    const today = new Date().getDay();
    return today === 0 || today === 6;
  }, []);

  const todayHoliday = useMemo(() => {
    const today = toLocalDateStr(new Date());
    return holidays.find(h => h.date === today);
  }, [holidays]);

  const upcomingHoliday = useMemo(() => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    return holidays.find(h => {
      const holidayDate = new Date(h.date);
      return holidayDate > today && holidayDate <= nextWeek;
    });
  }, [holidays]);

  const todayProjectCount = useMemo(() => {
    const today = toLocalDateStr(new Date());
    const projectIds = new Set();
    recentEntries
      .filter(e => e.start_time?.slice(0, 10) === today && e.duration > 60)
      .forEach(e => { if (e.project_id) projectIds.add(e.project_id); });
    return projectIds.size;
  }, [recentEntries]);

  const isThursdayOrFriday = useMemo(() => {
    const today = new Date().getDay();
    return today === 4 || today === 5;
  }, []);

  const dismissAlert = (alertId) => {
    const newDismissed = { ...dismissedAlerts, ids: [...dismissedAlerts.ids, alertId] };
    setDismissedAlerts(newDismissed);
    localStorage.setItem('dismissedAlerts', JSON.stringify(newDismissed));
  };

  const contextualAlerts = useMemo(() => {
    const alerts = [];
    if (pendingLeaveRequests.length > 0 && !dismissedAlerts.ids.includes('pending-leave')) {
      alerts.push({ id: 'pending-leave', type: 'warning', icon: AlertTriangle, message: `${pendingLeaveRequests.length} pending leave request${pendingLeaveRequests.length > 1 ? 's' : ''} awaiting approval`, link: '/time-off' });
    }
    if (approvedLeaveRequests.length > 0 && !dismissedAlerts.ids.includes('approved-leave')) {
      const recent = approvedLeaveRequests[0];
      const startDate = new Date(recent.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endDate = new Date(recent.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      alerts.push({ id: 'approved-leave', type: 'success', icon: CheckCircle2, message: `${recent.leave_type || 'Leave'} approved \u2014 ${startDate} to ${endDate}`, link: '/time-off' });
    }
    if (upcomingHoliday && !dismissedAlerts.ids.includes('upcoming-holiday')) {
      const holidayDate = new Date(upcomingHoliday.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      alerts.push({ id: 'upcoming-holiday', type: 'info', icon: PartyPopper, message: `${upcomingHoliday.name} on ${holidayDate} \u2014 office closed`, link: null });
    }
    if (!isWeekend && !todayHoliday && todayHours === 0 && !isRunning && !dismissedAlerts.ids.includes('no-time-today')) {
      alerts.push({ id: 'no-time-today', type: 'info', icon: Info, message: "You haven't tracked time today yet", link: '/tracker' });
    }
    return alerts.slice(0, 2);
  }, [pendingLeaveRequests, approvedLeaveRequests, upcomingHoliday, isWeekend, todayHoliday, todayHours, isRunning, dismissedAlerts]);

  // Compute today's progress percentage toward daily target
  const todayProgress = useMemo(() => {
    const current = todayHours + (isRunning ? elapsedTime : 0);
    const target = dailyHoursTarget * 3600;
    return Math.min(100, Math.round((current / target) * 100));
  }, [todayHours, isRunning, elapsedTime, dailyHoursTarget]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 18) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-400 font-medium">{t('common.loading')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Hero Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase">
              {getGreeting()}
            </p>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              {user?.name?.split(' ')[0]} — {t('dashboard.dashboard')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {formatFullDate(new Date())}
              {!isWeekend && !todayHoliday && (
                <span className="text-slate-400"> &middot; {uniqueProjectsThisWeek} project{uniqueProjectsThisWeek !== 1 ? 's' : ''} active this week</span>
              )}
            </p>
          </div>

          {/* Quick Action Buttons — hidden for stealth employees */}
          {!isStealthEmployee && (
          <div className="flex items-center gap-3">
            {isRunning ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 rounded-xl" data-testid="timer-running-indicator">
                <span className="w-2 h-2 rounded-full bg-blue-50 dark:bg-blue-900/30 animate-pulse" />
                <span className="text-sm font-semibold text-blue-700 timer-display">
                  {formatTime(elapsedTime)}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="h-7 px-2 text-blue-600 hover:bg-blue-100"
                  onClick={() => navigate('/tracker')}
                >
                  View
                </Button>
              </div>
            ) : (
              <Button 
                onClick={() => navigate('/tracker')} 
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-10 px-5 text-sm font-semibold shadow-sm"
                data-testid="start-tracking-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Tracking
              </Button>
            )}
          </div>
          )}
        </div>

        {/* Contextual Alerts */}
        {contextualAlerts.length > 0 && (
          <div className="flex flex-wrap gap-3" data-testid="contextual-alerts">
            {contextualAlerts.map((alert) => {
              const AlertIcon = alert.icon;
              const colorMap = {
                warning: 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 border-amber-200',
                success: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 border-emerald-200',
                error: 'bg-red-50 dark:bg-red-900/30 text-red-800 border-red-200',
                info: 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 border-blue-200'
              };
              return (
                <div 
                  key={alert.id}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium border ${colorMap[alert.type] || colorMap.info}`}
                  data-testid={`alert-${alert.id}`}
                >
                  <AlertIcon className="w-4 h-4 flex-shrink-0 opacity-70" />
                  {alert.link ? (
                    <Link to={alert.link} className="hover:underline underline-offset-2">{alert.message}</Link>
                  ) : (
                    <span>{alert.message}</span>
                  )}
                  <button onClick={(e) => { e.preventDefault(); dismissAlert(alert.id); }} className="ml-1 p-0.5 rounded hover:bg-black/5">
                    <X className="w-3.5 h-3.5 opacity-50" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-row">
          {/* This Week */}
          <div 
            className={`relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5 group ${
              (user?.role === 'admin' || user?.role === 'manager') ? 'cursor-pointer' : ''
            }`}
            style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
            onClick={() => (user?.role === 'admin' || user?.role === 'manager') && navigate('/admin/reports', { state: { filter: 'week' } })}
            data-testid="stat-total-hours"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 dark:bg-blue-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              {weekTrend && (
                <span className={`flex items-center gap-0.5 text-xs font-semibold ${weekTrend.isUp ? 'text-emerald-600' : 'text-red-500'}`}>
                  {weekTrend.isUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {weekTrend.pct}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white timer-display tracking-tight">
              {formatDuration(((timesheetStatus?.timesheet?.total_hours || 0) * 3600) + (isRunning ? elapsedTime : 0))}
            </p>
            <p className="text-xs font-medium text-slate-400 mt-1 uppercase tracking-wider">This Week</p>
          </div>

          {/* This Month */}
          <div 
            className={`relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5 group ${
              (user?.role === 'admin' || user?.role === 'manager') ? 'cursor-pointer' : ''
            }`}
            style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
            onClick={() => (user?.role === 'admin' || user?.role === 'manager') && navigate('/admin/reports', { state: { filter: 'month' } })}
            data-testid="stat-this-month"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 dark:bg-emerald-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                <CalendarDays className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white timer-display tracking-tight">
              {formatDuration(monthHours + (isRunning ? elapsedTime : 0))}
            </p>
            <p className="text-xs font-medium text-slate-400 mt-1 uppercase tracking-wider">
              {new Date().toLocaleString('en-US', { month: 'long' })}
            </p>
          </div>

          {/* Active Projects */}
          <div 
            className="relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5 cursor-pointer group"
            style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
            onClick={() => navigate('/projects')}
            data-testid="stat-projects"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-violet-50 dark:bg-violet-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center group-hover:bg-violet-100 transition-colors">
                <FolderKanban className="w-5 h-5 text-violet-600" />
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              {projects.length}
            </p>
            <p className="text-xs font-medium text-slate-400 mt-1 uppercase tracking-wider">Active Projects</p>
          </div>

          {/* Today */}
          <div 
            className="relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5 cursor-pointer group"
            style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
            onClick={() => navigate('/tracker')}
            data-testid="stat-today"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-amber-50 dark:bg-amber-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              {todayProgress > 0 && (
                <span className="text-xs font-semibold text-slate-400">{todayProgress}%</span>
              )}
            </div>
            <p className={`text-2xl font-bold timer-display tracking-tight ${
              (todayHours + (isRunning ? elapsedTime : 0)) > dailyHoursTarget * 3600 
                ? 'text-orange-500' : 'text-slate-900 dark:text-white'
            }`}>
              {formatDuration(todayHours + (isRunning ? elapsedTime : 0))}
            </p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Today</p>
              {todayProgress > 0 && (
                <div className="w-12 h-1 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${todayProgress}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timesheet Status Widget - hidden on free plan */}
        {!isFreePlan && timesheetStatus && (
          <div 
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5 cursor-pointer group"
            style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
            onClick={() => navigate('/approvals')}
            data-testid="timesheet-status-widget"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  timesheetStatus.timesheet?.status === 'approved' ? 'bg-emerald-50 dark:bg-emerald-900/30' :
                  timesheetStatus.timesheet?.status === 'submitted' ? 'bg-blue-50 dark:bg-blue-900/30' :
                  timesheetStatus.timesheet?.status === 'rejected' ? 'bg-red-50 dark:bg-red-900/30' : 'bg-amber-50 dark:bg-amber-900/30'
                }`}>
                  {timesheetStatus.timesheet?.status === 'approved' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> :
                   timesheetStatus.timesheet?.status === 'submitted' ? <Clock className="w-5 h-5 text-blue-600" /> :
                   timesheetStatus.timesheet?.status === 'rejected' ? <XCircle className="w-5 h-5 text-red-600" /> :
                   <ClipboardCheck className="w-5 h-5 text-amber-600" />}
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Weekly Timesheet</h3>
                    <span className="text-base font-bold text-slate-900 dark:text-white timer-display">
                      {formatDuration((timesheetStatus.timesheet?.total_hours || 0) * 3600)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2">
                    {timesheetStatus.timesheet?.status === 'approved' ? 'Approved' :
                     timesheetStatus.timesheet?.status === 'submitted' ? 'Awaiting approval' :
                     timesheetStatus.timesheet?.status === 'rejected' ? 'Changes requested' : 'Not submitted'}
                    {(!timesheetStatus.timesheet?.status || timesheetStatus.timesheet?.status === 'draft') && isThursdayOrFriday && (
                      <span className="text-amber-600 font-medium">Submit before end of week</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  timesheetStatus.timesheet?.status === 'approved' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700' :
                  timesheetStatus.timesheet?.status === 'submitted' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700' :
                  timesheetStatus.timesheet?.status === 'rejected' ? 'bg-red-50 dark:bg-red-900/30 text-red-700' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                }`}>
                  {timesheetStatus.timesheet?.status === 'approved' ? 'Approved' :
                   timesheetStatus.timesheet?.status === 'submitted' ? 'Pending' :
                   timesheetStatus.timesheet?.status === 'rejected' ? 'Rejected' : 'Draft'}
                </span>
                {(!timesheetStatus.timesheet?.status || timesheetStatus.timesheet?.status === 'draft') && (
                  <Button
                    size="sm"
                    className="h-9 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/approvals'); }}
                    data-testid="submit-timesheet-btn"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    Submit
                  </Button>
                )}
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 dark:text-slate-400 transition-colors" />
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid: Chart + Project Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Weekly Activity Chart */}
          <div 
            className="lg:col-span-8 bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-6"
            style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
            data-testid="weekly-chart"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">Weekly Activity</h3>
                <p className="text-xs text-slate-400 mt-0.5">Hours tracked per day</p>
              </div>
              <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                {[
                  { value: 'thisWeek', label: 'This Week' },
                  { value: 'lastWeek', label: 'Last Week' }
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setChartFilter(filter.value)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      chartFilter === filter.value
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
                    }`}
                    data-testid={`chart-filter-${filter.value}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[240px] relative">
              {chartFilter === 'thisWeek' && (summary?.total_duration || 0) < 1800 && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-700/90 rounded-lg z-10">
                  <div className="text-center px-4">
                    <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Start the timer to build your activity graph</p>
                  </div>
                </div>
              )}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartFilter === 'thisWeek' ? chartData : chartData.map(d => ({ ...d, hours: 0 }))} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <XAxis 
                    dataKey="day" axisLine={false} tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 500 }}
                  />
                  <YAxis 
                    axisLine={false} tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 500 }}
                    tickFormatter={(v) => `${v}h`} width={40}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)', radius: 8 }} />
                  {dailyAverage && chartFilter === 'thisWeek' && (
                    <ReferenceLine 
                      y={dailyAverage} stroke="#cbd5e1" strokeDasharray="6 4" 
                      label={{ value: `avg ${dailyAverage}h`, position: 'right', fill: '#94a3b8', fontSize: 10, fontWeight: 500 }}
                    />
                  )}
                  <Bar dataKey="hours" radius={[6, 6, 0, 0]} maxBarSize={48}>
                    {(chartFilter === 'thisWeek' ? chartData : chartData.map(d => ({ ...d, hours: 0 }))).map((entry, index) => {
                      const hasNoData = entry.hours === 0 || entry.hours < 0.01;
                      let fill = '#93c5fd';
                      let opacity = 1;
                      if (hasNoData) { fill = 'transparent'; opacity = 0; }
                      else if (entry.isToday) { fill = '#2563EB'; }
                      return <Cell key={`cell-${index}`} fill={fill} opacity={opacity} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Project Breakdown */}
          <div 
            className="lg:col-span-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-6 flex flex-col"
            style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
            data-testid="project-breakdown"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">By Project</h3>
                <p className="text-xs text-slate-400 mt-0.5">Time distribution this week</p>
              </div>
              {timesheetStatus?.project_breakdown && timesheetStatus.project_breakdown.length > 5 && (
                <Link to="/projects" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
            <div className="space-y-4 flex-1">
              {timesheetStatus?.project_breakdown?.slice(0, 5).map((project, index) => {
                const totalHours = timesheetStatus?.timesheet?.total_hours || 1;
                const percent = Math.round((project.hours / totalHours) * 100);
                const isTop = index === 0 && project.hours > 0;
                return (
                  <div key={project.id || index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: project.color || '#94a3b8' }} />
                        <span className={`text-sm text-slate-700 dark:text-slate-300 truncate max-w-[120px] ${isTop ? 'font-semibold' : 'font-medium'}`}>
                          {project.name}
                        </span>
                        {isTop && <Trophy className="w-3.5 h-3.5 text-amber-500" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 font-medium">{percent}%</span>
                        <span className="text-sm font-bold text-slate-900 dark:text-white timer-display">
                          {formatDuration(project.hours * 3600)}
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${percent}%`, backgroundColor: project.color || '#94a3b8' }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!timesheetStatus?.project_breakdown || timesheetStatus.project_breakdown.length === 0) && (
                <div className="text-center py-8 flex-1 flex flex-col items-center justify-center">
                  <FolderKanban className="w-8 h-8 text-slate-200 mb-2" />
                  <p className="text-sm text-slate-400">No project data yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div 
          className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 overflow-hidden"
          style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}
          data-testid="recent-entries"
        >
          <div className="flex items-center justify-between px-6 py-5">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">Recent Activity</h3>
              <p className="text-xs text-slate-400 mt-0.5">Your latest time entries</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'today', label: 'Today' }
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setActivityFilter(filter.value)}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                      activityFilter === filter.value
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
                    }`}
                    data-testid={`activity-filter-${filter.value}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <Link to="/tracker" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
                View all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          <div className="border-t border-slate-100 dark:border-slate-700">
            {validRecentEntries.map((entry) => {
              const project = getProjectById(entry.project_id);
              return (
                <div 
                  key={entry.id} 
                  className="flex items-center gap-4 px-6 py-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 dark:bg-slate-700/60 transition-colors"
                  data-testid={`entry-${entry.id}`}
                >
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: project?.color || '#94a3b8' }} />
                  <div className="flex-1 min-w-0">
                    {entry.description ? (
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{entry.description}</p>
                    ) : (
                      <p className="text-sm italic text-slate-400 truncate">No description</p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">
                      {project?.name || 'No project'} &middot; {formatDate(entry.start_time)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-slate-900 dark:text-white timer-display">{formatDuration(entry.duration)}</p>
                    <p className="text-xs text-slate-400 mt-0.5 timer-display">
                      {formatTimeOnly(entry.start_time)} - {entry.end_time ? formatTimeOnly(entry.end_time) : 'Running'}
                    </p>
                  </div>
                </div>
              );
            })}
            {validRecentEntries.length === 0 && (
              <div className="text-center py-14">
                <Clock className="w-8 h-8 text-slate-200 mx-auto mb-3" />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">No time entries yet</h3>
                <p className="text-xs text-slate-400 mb-4">Start tracking to see activity here</p>
                <Link to="/tracker" className="text-sm text-blue-600 hover:text-blue-700 font-semibold">
                  Go to Time Tracker
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
